import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import EditIcon from '@mui/icons-material/Edit';
import StatusBadge from '../../../Components/Badges/StatusBadge';
import AddMedical from './AddMedical';
import EditMedical from './EditMedical';
import axiosInstance from '../../../Config/axiosInstance';
import './Medical.css';

const Medical = ({
  employee,
  medicalRecords = [],
  showAddMedical,
  setShowAddMedical,
  onEditSuccess,
}) => {

  // state pentru clinici + modale + permisiuni + înregistrare editată
  const [suppliers, setSuppliers] = useState({});
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditMedical, setShowEditMedical] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(false);

  // verifică permisiuni din localStorage
  useEffect(() => {
    const requiredPath = "/employees/:id/medical";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const checkPermission = (key) =>
      permissions.some(page => {
        try {
          const pagePath = new URL(page.url).pathname;
          return pagePath === requiredPath && page[key] === true;
        } catch {
          return false;
        }
      });

    setCanCreate(checkPermission('can_create'));
    setCanEdit(checkPermission('can_edit'));
    setCanView(checkPermission('can_view'));
  }, []);

  // fetch clinici
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axiosInstance.get('/suppliers/');
        const suppliersMap = response.data.reduce((acc, supplier) => {
          acc[supplier.id] = supplier.name || supplier.denumire || '-';
          return acc;
        }, {});
        setSuppliers(suppliersMap);
      } catch (error) {
        console.error('❌ Eroare la preluarea clinicilor:', error);
      }
    };
    fetchSuppliers();
  }, []);

  // mapare status pentru badge
  const getStatusVariant = (status) => {
    if (!status) return 'default';
    const normalized = status.trim().toLowerCase();
    switch (normalized) {
      case 'expired':
      case 'expirat':
      case 'inactive':
      case 'critical':
        return 'critical';
      case 'pending':
        return 'pending';
      case 'scheduler':
        return 'warning';
      case 'completed':
      case 'active':
        return 'success';
      default:
        return 'default';
    }
  };

  // deschidere document în tab nou
  const handleDocumentClick = (url) => {
    if (!url) return;
    const base = 'https://erp-pmpc.ro'
    let fullUrl = url;

    if (url.startsWith("/")) fullUrl = `${base}${url}`;
    else if (!url.startsWith("http")) fullUrl = `${base}/media/${url}`;

    window.open(fullUrl, "_blank");
  };

  // închidere modale
  const handleCloseModal = () => {
    setShowAddMedical(false);
    setEditingRecord(null);
  };

  // reîncarcă pagina după creare
  const handleMedicalDataUpdated = () => {
    setShowAddMedical(false);
    setEditingRecord(null);
    window.location.reload();
  };

  // click pe Edit → trimite cel mai nou record
  const handleEditClick = () => {
    if (!canEdit) return;
    setEditingRecord(latestRecord);
    setShowEditMedical(true);
  };

  const handleCloseEdit = () => {
    setShowEditMedical(false);
    setEditingRecord(null);
  };

  // numele angajatului
  const employeeName =
    employee?.full_name ||
    `${employee?.first_name || ''} ${employee?.last_name || ''}` ||
    'Employee';

  // verificare existență înregistrări
  const hasRecords = Array.isArray(medicalRecords) && medicalRecords.length > 0;

  // determină cel mai recent record medical
  const latestRecord = hasRecords
    ? [...medicalRecords].sort((a, b) => {
        const dateA = new Date(a.date || a.created_at || a.exam_date || 0);
        const dateB = new Date(b.date || b.created_at || b.exam_date || 0);
        return dateB - dateA;
      })[0]
    : null;

  // calculează câte zile rămân până la expirare
  const calculateCountdown = (expireDate) => {
    if (!expireDate) return '-';
    const now = new Date();
    const exp = new Date(expireDate);
    const diff = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Expired';
    if (diff <= 30) return `${diff} days remaining ⚠️`;
    return `${diff} days remaining`;
  };

  // nume clinică din ID
  const getClinicName = (clinicIdOrName) => {
    if (!clinicIdOrName) return '-';
    return suppliers[clinicIdOrName] || clinicIdOrName || 'Clinic not found';
  };

  // dacă nu are permisiune de vizualizare
  if (!canView) {
    return (
      <div className="page-medical">
        <div className="medical-container">
          <div className="medical-empty">
            <MedicalServicesOutlinedIcon
              className="medical-empty-icon"
              sx={{ fontSize: 48 }}
            />
            <h3>Access Restricted</h3>
            <p>You do not have permission to view medical records for this employee.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-medical">
      <div className="medical-container">
        
        {/* afișare ultim record sau mesaj dacă nu există */}
        {latestRecord ? (
          <div className="medical-list">
            <div key={latestRecord.id || Math.random()} className="medical-card">

              {/* Status */}
              <div className="medical-row">
                <div className="medical-label">Status</div>
                <StatusBadge
                  variant={getStatusVariant(latestRecord.status || latestRecord.result)}
                  text={
                    latestRecord.status
                      ? latestRecord.status.charAt(0).toUpperCase() + latestRecord.status.slice(1).toLowerCase()
                      : latestRecord.result
                        ? latestRecord.result.charAt(0).toUpperCase() + latestRecord.result.slice(1).toLowerCase()
                        : '-'
                  }
                />
              </div>

              {/* Data */}
              <div className="medical-row">
                <div className="medical-label">Date</div>
                <div className="medical-text">
                  {latestRecord.date
                    ? new Date(latestRecord.date).toLocaleDateString('ro-RO')
                    : '-'}
                </div>
              </div>

              {/* Ora */}
              <div className="medical-row">
                <div className="medical-label">Time</div>
                <div className="medical-text">
                  {latestRecord.hour || latestRecord.time || '-'}
                </div>
              </div>

              {/* Clinica */}
              <div className="medical-row">
                <div className="medical-label">Clinic name</div>
                <div className="medical-text">
                  {getClinicName(latestRecord.clinic || latestRecord.clinic_id)}
                </div>
              </div>

              {/* Document principal */}
              <div className="medical-row">
                <div className="medical-label">Documents</div>
                <div className="medical-docs">
                  {latestRecord.documents ? (
                    <div
                      className="medical-link"
                      onClick={() =>
                        handleDocumentClick(
                          latestRecord.documents.url || latestRecord.documents
                        )
                      }
                    >
                      {latestRecord.documents.name ||
                        latestRecord.documents.split('/').pop()}{' '}
                      <FileText size={16} />
                    </div>
                  ) : (
                    <span className="medical-text">-</span>
                  )}
                </div>
              </div>

              {/* Rezultat medical */}
              <div className="medical-row">
                <div className="medical-label">Medical examination result</div>
                {latestRecord.result ? (
                  typeof latestRecord.result === 'string' && latestRecord.result.startsWith('/') ? (
                    <div
                      className="medical-link"
                      onClick={() => handleDocumentClick(latestRecord.result)}
                    >
                      {latestRecord.result.split('/').pop()} <FileText size={16} />
                    </div>
                  ) : (
                    <span className="medical-text">{latestRecord.result}</span>
                  )
                ) : (
                  <span className="medical-text">-</span>
                )}
              </div>

              {/* Expirare */}
              <div className="medical-row">
                <div className="medical-label">Expiration date</div>
                <div className="medical-text">
                  {latestRecord.expire_date
                    ? new Date(latestRecord.expire_date).toLocaleDateString('ro-RO')
                    : '-'}
                </div>
              </div>

              {/* Alertă */}
              <div className="medical-row">
                <div className="medical-label">Alert</div>
                <div
                  className={`medical-text medical-countdown ${
                    calculateCountdown(latestRecord.expire_date).includes('Expired')
                      ? 'expired'
                      : ''
                  }`}
                >
                  {calculateCountdown(latestRecord.expire_date)}
                </div>
              </div>

              {/* Buton Edit */}
              {canEdit && (
                <div className="medical-footer">
                  <button
                    className="medical-edit-btn"
                    onClick={handleEditClick}
                    title="Edit record"
                  >
                    <EditIcon fontSize="small" />
                    EDIT
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (

          // dacă nu există înregistrări
          <div className="medical-empty">
            <MedicalServicesOutlinedIcon
              className="medical-empty-icon"
              sx={{ fontSize: 48 }}
            />
            <h3>No Medical Records Found</h3>
            <p>No Medical records have been created for {employeeName} yet.</p>

            {canCreate && (
              <button
                className="medical-create-btn"
                onClick={() => setShowAddMedical(true)}
              >
                Create New Record
              </button>
            )}
          </div>
        )}

        {/* modal add */}
        {canCreate && (
          <AddMedical
            isOpen={showAddMedical}
            onClose={handleCloseModal}
            onMedicalDataUpdated={handleMedicalDataUpdated}
            employeeData={employee}
          />
        )}

        {/* modal edit */}
        {canEdit && (
          <EditMedical
            isOpen={showEditMedical}
            onClose={handleCloseEdit}
            recordData={editingRecord}
            employeeData={employee}
            onSuccess={onEditSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default Medical;
